import { useState, ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/utils';

export interface SJAccordionItem {
  id: string;
  title: string;
  content: ReactNode;
  disabled?: boolean;
}

export interface SJAccordionProps {
  items: SJAccordionItem[];
  allowMultiple?: boolean;
  className?: string;
}

export const SJAccordion = ({
  items,
  allowMultiple = false,
  className,
}: SJAccordionProps) => {
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());

  const toggleItem = (itemId: string) => {
    const item = items.find((i) => i.id === itemId);
    if (item && item.disabled) {
      return;
    }

    setOpenIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        if (!allowMultiple) {
          newSet.clear();
        }
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  return (
    <div className={className}>
      {items.map((item, index) => (
        <div
          key={item.id}
          className={cn(
            index !== items.length - 1 && 'border-b border-[var(--sj-border)]'
          )}
        >
          <button
            onClick={() => toggleItem(item.id)}
            disabled={item.disabled}
            className={cn(
              'w-full flex items-center justify-between py-4 text-sm font-medium outline-none transition-colors',
              item.disabled
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:text-primary-600',
              'focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2'
            )}
            aria-expanded={openIds.has(item.id)}
            aria-controls={`accordion-content-${item.id}`}
          >
            <span>{item.title}</span>
            <motion.div
              animate={{ rotate: openIds.has(item.id) ? 180 : 0 }}
              transition={{ duration: 0.2 }}
              className="flex-shrink-0 ml-2"
            >
              <ChevronDown className="h-5 w-5" />
            </motion.div>
          </button>

          <AnimatePresence initial={false}>
            {openIds.has(item.id) && (
              <motion.div
                key={`content-${item.id}`}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                id={`accordion-content-${item.id}`}
              >
                <div className="pb-4 text-sm text-[var(--sj-text-secondary)]">
                  {item.content}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

SJAccordion.displayName = 'SJAccordion';
